import axiosBase, { AxiosRequestConfig } from "axios";
import { emitAuthEvent } from "Components/Auth/authEvents";

const axios = axiosBase.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "x-api-key": process.env.REACT_APP_X_API_KEY,
    "Content-Type": "application/json",
  },
});

// Unified interceptor - always unwraps response.data
axios.interceptors.response.use(
  (response) => {
    // Unwrap data consistently
    return response.data ? response.data : response;
  },
  (error) => {
    const status = error.response?.status;
    let message;

    switch (status) {
      case 401:
        message = "Invalid credentials";
        // Clear auth and redirect only if not on auth pages
        const authPages = [
          "/login",
          "/register",
          "/forgot-password",
          "/reset-password",
        ];
        const currentPath = window.location.pathname;

        if (!authPages.includes(currentPath)) {
          emitAuthEvent("UNAUTHORIZED");
        }
        break;
      case 404:
        message = "Sorry! The data you are looking for could not be found";
        break;
      case 500:
        message = "Internal Server Error";
        break;
      default:
        message = error.response?.data?.message || error.message || error;
    }

    return Promise.reject(error.response?.data || message);
  },
);

/**
 * Sets the default authorization
 */
const setAuthorization = (token?: string) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

class APIClient {
  get = <T>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> => {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key + "[]", v.toString()));
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const queryString = queryParams.toString();
    return axios.get(queryString ? `${url}?${queryString}` : url, config);
  };

  create = <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<T> => {
    return axios.post(url, data, config);
  };

  update = <T>(url: string, data: any): Promise<T> => {
    return axios.patch(url, data);
  };

  put = <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<T> => {
    return axios.put(url, data, config);
  };

  delete = <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return axios.delete(url, config);
  };
}

export { APIClient, setAuthorization };
